import React, { useCallback } from 'react';
import { EnterpriseFormModal, FieldHint, type FormModalMode } from 'components/enterprise-shell';
import { SyutSelect } from 'components/ui';
import { chargeTypeService } from 'api/services/chargeTypeService';
import {
  REVENUE_GROUP_OPTIONS,
  SUPPLY_NATURE_OPTIONS,
  CHARGE_NATURE_OPTIONS,
  type ChargeNature,
  type ChargeRevenueGroup,
  type ChargeType,
} from 'api/types/chargeType.types';

/**
 * ============================================================================
 * CHARGE TYPE FORM MODAL
 * ============================================================================
 * create → a new charge type; copy → a new one prefilled from another (new code);
 * edit   → everything but the code, which is permanent (folio charges, outlets and
 *          tax mappings refer to it). A system type (Room, Meal plan, Add-on,
 *          Restaurant) keeps its name, group and flags — only description and
 *          display order change. The server enforces all of it; its refusal text
 *          is shown as the error.
 * ============================================================================
 */

export interface ChargeTypeForm {
  chargeCode: string;
  chargeName: string;
  description: string;
  revenueGroup: ChargeRevenueGroup;
  isGuestBillable: boolean;
  allowManualPost: boolean;
  /** Place-of-supply nature; '' = Accommodation. */
  supplyNature: string;
  chargeNature: ChargeNature;
  displayOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: ChargeTypeForm = {
  chargeCode: '',
  chargeName: '',
  description: '',
  revenueGroup: 'OTHER',
  isGuestBillable: true,
  allowManualPost: true,
  supplyNature: 'SERVICE_GENERAL',
  chargeNature: 'SERVICE',
  displayOrder: '100',
  isActive: true,
};

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,19}$/;

interface Props {
  isOpen: boolean;
  mode: FormModalMode;
  record: ChargeType | null;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

const Check: React.FC<{
  label: string; hint: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void;
}> = ({ label, hint, checked, disabled, onChange }) => (
  <div className="form-group">
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: disabled ? 'default' : 'pointer', minHeight: '2.25rem' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
      />
      <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{label}</span>
    </label>
    <FieldHint>{hint}</FieldHint>
  </div>
);

const ChargeTypeFormModal: React.FC<Props> = ({
  isOpen, mode, record, tabId, moduleName, createInstanceId, onClose, onSaved, onRestoreModal,
}) => {
  const populateOnOpen = useCallback((m: FormModalMode, r: ChargeType | null): ChargeTypeForm => {
    if (!r || m === 'create') return { ...EMPTY_FORM };
    return {
      chargeCode: m === 'copy' ? '' : r.chargeCode,
      chargeName: m === 'copy' ? '' : r.chargeName,
      description: r.description ?? '',
      revenueGroup: r.revenueGroup,
      isGuestBillable: r.isGuestBillable,
      // A copy of a system type is an ordinary type: start it manually postable.
      allowManualPost: m === 'copy' && r.isSystem ? true : r.allowManualPost,
      supplyNature: r.supplyNature ?? '',
      chargeNature: r.chargeNature ?? 'SERVICE',
      displayOrder: String(r.displayOrder),
      isActive: m === 'copy' ? true : r.isActive,
    };
  }, []);

  const validate = useCallback((f: ChargeTypeForm, m: FormModalMode): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (m !== 'edit') {
      const code = f.chargeCode.trim().toUpperCase();
      if (!code) errors.chargeCode = 'Code is required.';
      else if (!CODE_PATTERN.test(code)) errors.chargeCode = '2–20 capital letters, digits or underscores, starting with a letter.';
    }
    if (!f.chargeName.trim()) errors.chargeName = 'Name is required.';
    const order = Number(f.displayOrder);
    if (!Number.isInteger(order) || order < 0) errors.displayOrder = 'A whole number, 0 or more.';
    return errors;
  }, []);

  const onSave = useCallback(async (f: ChargeTypeForm, m: FormModalMode, r: ChargeType | null) => {
    const common = {
      chargeName: f.chargeName.trim(),
      description: f.description.trim() || null,
      revenueGroup: f.revenueGroup,
      isGuestBillable: f.isGuestBillable,
      allowManualPost: f.allowManualPost,
      supplyNature: f.supplyNature || null,
      chargeNature: f.chargeNature,
      displayOrder: Number(f.displayOrder),
      isActive: f.isActive,
    };
    if (m === 'edit' && r) {
      await chargeTypeService.update(r.id, { ...common, rowVersion: r.rowVersion });
      return;
    }
    await chargeTypeService.create({ ...common, chargeCode: f.chargeCode.trim().toUpperCase() });
  }, []);

  return (
    <EnterpriseFormModal<ChargeTypeForm, ChargeType>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun="Charge Type"
      emptyForm={EMPTY_FORM}
      populateOnOpen={populateOnOpen}
      validate={validate}
      onSave={onSave}
      confirmSaveMessage={(m, f) => (m === 'edit'
        ? `Save changes to ${f.chargeName.trim() || 'this charge type'}?`
        : `Add charge type ${f.chargeCode.trim().toUpperCase()} — ${f.chargeName.trim()}? The code cannot be changed later.`)}
      size="md"
      tabId={tabId}
      moduleName={moduleName}
      createInstanceId={createInstanceId}
      recordKey={record?.id}
      getStripFormLabel={(f) => f.chargeName || f.chargeCode || undefined}
      getStripRecordLabel={(r) => (r ? r.chargeName : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {({ form, setForm, mode: m, record: r, getFieldError, clearFieldError, isSaving }) => {
        const set = <K extends keyof ChargeTypeForm>(key: K, value: ChargeTypeForm[K]) => {
          clearFieldError(key as string);
          setForm((prev) => ({ ...prev, [key]: value }));
        };
        // A system type: only description and display order are editable.
        const locked = m === 'edit' && Boolean(r?.isSystem);
        const lockedHint = 'System charge type — used by CRS itself.';

        return (
          <>
            {locked && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', background: 'var(--bg-secondary)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {r?.chargeName} is a system charge type. Only its description and display order can change.
              </div>
            )}
            <div className="form-grid-2">
              <div className="form-group" data-field="chargeCode">
                <label className="form-label">Code {m !== 'edit' && <span className="text-danger">*</span>}</label>
                <input
                  type="text"
                  className={`form-input${getFieldError('chargeCode') ? ' form-field-error' : ''}`}
                  value={form.chargeCode}
                  maxLength={20}
                  placeholder="e.g., LAUNDRY"
                  onChange={(e) => set('chargeCode', e.target.value.toUpperCase().replace(/\s/g, '_'))}
                  readOnly={m === 'edit'}
                  disabled={isSaving}
                  style={m === 'edit' ? { background: 'var(--bg-secondary)', fontFamily: 'monospace' } : { fontFamily: 'monospace' }}
                />
                {getFieldError('chargeCode')
                  ? <div className="form-error-message">{getFieldError('chargeCode')}</div>
                  : <FieldHint>{m === 'edit' ? 'Permanent: folio charges and tax rules refer to it.' : 'Permanent once saved, and never reused.'}</FieldHint>}
              </div>
              <div className="form-group" data-field="chargeName">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input${getFieldError('chargeName') ? ' form-field-error' : ''}`}
                  value={form.chargeName}
                  maxLength={100}
                  placeholder="Shown on the folio and the statement"
                  onChange={(e) => set('chargeName', e.target.value)}
                  readOnly={locked}
                  disabled={isSaving}
                  style={locked ? { background: 'var(--bg-secondary)' } : undefined}
                />
                {getFieldError('chargeName') && <div className="form-error-message">{getFieldError('chargeName')}</div>}
              </div>
              <div className="form-group" data-field="revenueGroup">
                <SyutSelect
                  label={<>Revenue group <span className="text-danger">*</span></>}
                  options={REVENUE_GROUP_OPTIONS}
                  value={form.revenueGroup}
                  onChange={(v) => set('revenueGroup', ((v as string) || 'OTHER') as ChargeRevenueGroup)}
                  isClearable={false}
                  isDisabled={locked || isSaving}
                  menuPortalTarget={document.body}
                  helperText="Groups revenue on reports."
                />
              </div>
              <div className="form-group" data-field="supplyNature">
                <SyutSelect
                  label="Place of supply (tax)"
                  options={SUPPLY_NATURE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  value={form.supplyNature || 'ACCOMMODATION'}
                  onChange={(v) => set('supplyNature', (v as string) || '')}
                  isClearable={false}
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  helperText={SUPPLY_NATURE_OPTIONS.find((o) => o.value === (form.supplyNature || 'ACCOMMODATION'))?.hint
                    ?? 'Decides CGST + SGST or IGST on charges posted at the desk.'}
                />
              </div>
              <div className="form-group" data-field="chargeNature">
                <SyutSelect
                  label="Nature"
                  options={CHARGE_NATURE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  value={form.chargeNature}
                  onChange={(v) => set('chargeNature', ((v as string) || 'SERVICE') as ChargeNature)}
                  isClearable={false}
                  isDisabled={locked || isSaving}
                  menuPortalTarget={document.body}
                  helperText={CHARGE_NATURE_OPTIONS.find((o) => o.value === form.chargeNature)?.hint}
                />
              </div>
              <div className="form-group" data-field="displayOrder">
                <label className="form-label">Display order</label>
                <input
                  type="number"
                  className={`form-input${getFieldError('displayOrder') ? ' form-field-error' : ''}`}
                  value={form.displayOrder}
                  min={0}
                  step={10}
                  onChange={(e) => set('displayOrder', e.target.value)}
                  disabled={isSaving}
                />
                {getFieldError('displayOrder')
                  ? <div className="form-error-message">{getFieldError('displayOrder')}</div>
                  : <FieldHint>Lower comes first in pickers and on the statement.</FieldHint>}
              </div>
              <Check
                label="Billed to the guest or a party"
                hint={locked ? lockedHint : 'Off = internal (house use, complimentary cost): recorded, never invoiced.'}
                checked={form.isGuestBillable}
                disabled={locked || isSaving}
                onChange={(v) => set('isGuestBillable', v)}
              />
              <Check
                label="Front Office may post it by hand"
                hint={locked ? lockedHint : 'Off when only a module or night audit posts it.'}
                checked={form.allowManualPost}
                disabled={locked || isSaving}
                onChange={(v) => set('allowManualPost', v)}
              />
              <Check
                label="Active"
                hint={locked ? lockedHint : 'Inactive types cannot be posted; past charges stay readable.'}
                checked={form.isActive}
                disabled={locked || isSaving}
                onChange={(v) => set('isActive', v)}
              />
            </div>
            <div className="form-group" data-field="description" style={{ marginTop: '0.5rem' }}>
              <label className="form-label">Description</label>
              <textarea
                className="form-input form-textarea"
                rows={2}
                maxLength={500}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="When to use this charge type"
                disabled={isSaving}
              />
            </div>
          </>
        );
      }}
    </EnterpriseFormModal>
  );
};

export default ChargeTypeFormModal;
